import type { PhoneElements } from './types'

/**
 * UI-менеджер для поля ввода телефона
 * Обрабатывает все UI-взаимодействия, рендеринг и манипуляции с DOM
 */
export class PhoneUIManager {
  private elements: PhoneElements
  private isPasting: boolean = false
  private readonly allowedControlKeys: Set<string> = new Set([
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'Tab',
    'Enter'
  ])

  constructor(elements: PhoneElements) {
    this.elements = elements
  }

  /**
   * Установить все обработчики событий
   * @param {object} callbacks - Обработчики событий-колбэков
   */
  public setupEventListeners(callbacks: {
    onPhoneInput: (
      _value: string,
      _cursorPosition: number,
      _inputType?: string
    ) => {
      newValue: string
      newCursorPosition: number
    }
    onPaste: (_value: string) => string
    onPlus?: () => void
  }): void {
    this.elements.phoneInput.addEventListener('keydown', (e) => {
      const target = e.target as HTMLInputElement

      if (e.ctrlKey || e.metaKey || e.altKey) {
        return
      }

      if (this.allowedControlKeys.has(e.key)) {
        return
      }

      // Мобильные клавиатуры (Android) шлют keydown с key=Unidentified/Process:
      // сам символ придёт в input, где лишнее отбросит маска. Отменять такое
      // нажатие нельзя — ввод не прошёл бы вовсе.
      if (e.key.length !== 1) {
        return
      }

      if (/^\d$/.test(e.key)) {
        return
      }

      if (e.key === '+') {
        e.preventDefault()
        // «+» нажат руками — номер набирают в международном виде.
        callbacks.onPlus?.()
        if (!target.value.startsWith('+')) {
          target.value = `+${target.value.replace(/^\+/, '')}`
        }

        const nextPosition = Math.max(1, target.selectionStart || 0)
        target.setSelectionRange(nextPosition, nextPosition)
        return
      }

      e.preventDefault()
    })

    // Изменения в поле ввода телефона
    this.elements.phoneInput.addEventListener('input', (e) => {
      // Игнорируем input во время paste - обработаем в обработчике paste
      if (this.isPasting) {
        return
      }

      const target = e.target as HTMLInputElement
      const value = target.value
      const inputEvent = e as InputEvent

      const cursorPosition = target.selectionStart || 0

      // Автозаполнение Chrome приходит input-ом раньше change — разбираем его
      // как вставку, иначе сохранённый +380… стал бы +7 (380)….
      const result =
        inputEvent.inputType === 'insertReplacementText'
          ? callbacks.onPhoneInput(
              callbacks.onPaste(value),
              cursorPosition,
              inputEvent.inputType
            )
          : callbacks.onPhoneInput(value, cursorPosition, inputEvent.inputType)
      target.value = result.newValue
      target.setSelectionRange(
        result.newCursorPosition,
        result.newCursorPosition
      )
    })

    // Обработка вставки (Ctrl+V)
    this.elements.phoneInput.addEventListener('paste', (e) => {
      e.preventDefault()
      this.isPasting = true

      const pastedText = e.clipboardData?.getData('text') || ''
      const cleanedValue = callbacks.onPaste(pastedText)

      const target = e.target as HTMLInputElement
      const cursorPosition = target.selectionStart || 0

      const result = callbacks.onPhoneInput(
        cleanedValue,
        cursorPosition,
        'insertFromPaste'
      )
      target.value = result.newValue
      target.setSelectionRange(
        result.newCursorPosition,
        result.newCursorPosition
      )

      // Сбрасываем флаг после завершения операции
      setTimeout(() => {
        this.isPasting = false
      }, 0)
    })

    // Обработка автозаполнения (change срабатывает после autofill)
    this.elements.phoneInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement
      const value = target.value

      this.isPasting = true

      const cleanedValue = callbacks.onPaste(value)

      const result = callbacks.onPhoneInput(
        cleanedValue,
        0,
        'insertReplacementText'
      )
      target.value = result.newValue

      // Сбрасываем флаг после завершения операции
      setTimeout(() => {
        this.isPasting = false
      }, 0)
    })
  }

  /**
   * Обновить placeholder поля ввода телефона
   * @param {string} placeholder - Текст placeholder для установки
   */
  public updatePlaceholder(placeholder: string): void {
    this.elements.phoneInput.placeholder = placeholder
  }

  /**
   * Установить значение поля ввода телефона
   * @param {string} value - Значение для установки
   */
  public setPhoneValue(value: string): void {
    this.elements.phoneInput.value = value
  }

  /**
   * Получить значение поля ввода телефона
   * @return {string} Текущее значение поля ввода
   */
  public getPhoneValue(): string {
    return this.elements.phoneInput.value
  }

  /**
   * Установить состояние ошибки
   * @param {boolean} hasError - Показывать ли состояние ошибки
   */
  public setError(hasError: boolean): void {
    if (hasError) {
      this.elements.wrapper.classList.add('error')
    } else {
      this.elements.wrapper.classList.remove('error')
    }
  }
}
